import React from 'react';
import {Box} from '@mui/material';
import {Typography} from "@mui/joy";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import _ from "lodash";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import {EventNoteRounded} from "@mui-symbols-material/w300";
import {WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import {HeroCardHeader, ViewAllLink} from "../dashboardHero/heroPrimitives";
import {SkeletonListRows, fadeInSx} from "../dashboardHero/HeroSkeletons";
import {InstalledBaseEventList} from "../../graphql/queries/installedBaseEventList";
import {AssetAssetLocation} from "../../graphql/queries/assetAssetLocation";
import formatEventEnum from "../../utils/formatEventEnum";
import {withVisibleEventCategories} from "../../utils/eventListFilter";

interface Props {
    assetLocationId: string;
    installedBaseNodeId: string | null;
    compact?: boolean;
}

/** Recent events as a vertical timeline (primary-ringed dots + connector). Fewer entries when compact. */
const AssetEventHistoryWidget = ({assetLocationId, installedBaseNodeId, compact}: Props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const maxEntries = compact ? 3 : 5;

    const assetLocationQuery = useQuery(AssetAssetLocation, {
        variables: {id: assetLocationId},
        skip: !assetLocationId,
    });
    const assetId = _.get(assetLocationQuery, 'data.assetLocation.asset.id');

    const eventListQuery = useQuery(InstalledBaseEventList, {
        variables: {
            filter: withVisibleEventCategories({
                assetId: {equals: assetId},
                installedBaseNodeId: {equals: installedBaseNodeId}
            }),
            pagination: {start: 0, limit: maxEntries},
            sort: {insert: "desc"}
        },
        skip: !assetId,
    });

    const total = _.get(eventListQuery, 'data.installedBaseEventList.total', 0);
    const events = _.get(eventListQuery, 'data.installedBaseEventList.installedBaseEvents', []);

    if (eventListQuery.error) {
        return null;
    }

    const loading = assetLocationQuery.loading || eventListQuery.loading;
    const goToList = () => navigate(`/assets/${assetLocationId}/events`);

    return (
        <WidgetBox>
            <HeroCardHeader
                title={t("Event history")}
                icon={<EventNoteRounded/>}
                count={total > 0 ? total : undefined}
                dense={compact}
            />

            {loading && !eventListQuery.data ? (
                <SkeletonListRows rows={compact ? 2 : 3} variant="timeline" />
            ) : total === 0 ? (
                <Typography level={"bodySmall"} sx={{color: "#777", pt: "12px"}}>
                    {t("No events yet")}
                </Typography>
            ) : (
                <Box sx={fadeInSx}>
                    <Box sx={{pl: "6px", pt: "12px"}}>
                        {events.map((event: any, index: number) => {
                            const meta = [event.message, event.user?.name].filter(Boolean).join(' · ');
                            return (
                                <Box key={event.id} sx={{display: "flex", gap: "12px"}}>
                                    <Box sx={{display: "flex", flexDirection: "column", alignItems: "center"}}>
                                        <Box sx={{width: 12, height: 12, borderRadius: "50%", border: "2px solid", borderColor: "primary.main", backgroundColor: "#fff", flex: "none", mt: "3px"}} />
                                        {index < events.length - 1 && <Box sx={{width: 2, flex: 1, backgroundColor: "#e0e0e0", my: "2px"}} />}
                                    </Box>
                                    <Box sx={{pb: "16px", flex: 1, minWidth: 0}}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                            <Typography level={"bodyBold"} sx={{color: "#333"}} noWrap>
                                                {formatEventEnum(event.eventType, t)}
                                            </Typography>
                                            <Typography level={"bodySmall"} sx={{color: "#999", ml: "auto", whiteSpace: "nowrap"}}>
                                                {moment(event.insert).format('DD.MM.YYYY')}
                                            </Typography>
                                        </Box>
                                        {meta && (
                                            // Truncated to one line (the layout must not grow with the
                                            // message); the full text is reachable via the title tooltip
                                            // and the event history list.
                                            <Typography level={"bodySmall"} sx={{color: "#777"}} noWrap title={meta}>
                                                {meta}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <ViewAllLink label={`${t("View full event history")} (${total})`} onClick={goToList} />
                </Box>
            )}
        </WidgetBox>
    );
};

export default AssetEventHistoryWidget;
